import React, { useState } from 'react';
import { Fab, Tooltip } from '@mui/material';
import { Share as ShareIcon } from '@mui/icons-material';
import ShareExternalUserDialog, {
  ShareExternalUserModule,
} from '../../dialogs/share/ShareExternalUserDialog';

interface ShareExternalUserFabProps {
  module: ShareExternalUserModule;
  /** Required for `PROJECT_DETAILS`; ignored otherwise. */
  projectId?: string | null;
  /** Label shown in the dialog header / WhatsApp message (e.g. project name). */
  subjectLabel?: string;
  /**
   * Custom tooltip. Defaults to `Share <Module>` so the same component reads
   * naturally on every page.
   */
  tooltip?: string;
  /** Zero-based position in the compact bottom-right action stack. */
  stackIndex?: number;
}

/**
 * Floating action button that opens the `ShareExternalUserDialog`. Used on
 * the agent-side Site Plans, Project Details, and Project Research pages.
 * Position is `fixed` bottom-right with a healthy z-index so the FAB sits
 * above page content but below MUI modal layers.
 */
export const ShareExternalUserFab: React.FC<ShareExternalUserFabProps> = ({
  module,
  projectId,
  subjectLabel,
  tooltip,
  stackIndex = 0,
}) => {
  const [open, setOpen] = useState(false);

  const label =
    tooltip ||
    (module === 'PICKER_SITE_PLANS'
      ? 'Share Site Plans'
      : module === 'PICKER_PROJECT_DETAILS'
      ? 'Share Project Details'
      : 'Share Project Research');

  return (
    <>
      <Tooltip title={label} placement="left">
        <Fab
          aria-label={label}
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            // Reserve one FAB slot for the host app's bottom-right +/- control
            // on mobile and tablet. Desktop keeps the standard page inset.
            bottom: { xs: 56 + (stackIndex * 68), md: 24 + (stackIndex * 68) },
            right: { xs: 16, sm: 24 },
            zIndex: (theme) => theme.zIndex.speedDial,
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(0.95)' },
          }}
        >
          <ShareIcon />
        </Fab>
      </Tooltip>
      <ShareExternalUserDialog
        open={open}
        onClose={() => setOpen(false)}
        module={module}
        projectId={projectId}
        subjectLabel={subjectLabel}
      />
    </>
  );
};

export default ShareExternalUserFab;
